
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import seshPromo from "@/assets/sesh-promo.png";

interface ProcessingStatusProps {
  isProcessing: boolean;
  progress: number;
  error: string | null;
  onRetry?: () => void;
  processingDuration: number;
}

export const ProcessingStatus = ({
  isProcessing,
  progress,
  error,
  onRetry,
  processingDuration,
}: ProcessingStatusProps) => {
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Processing Error</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              disabled={isProcessing}
              className="ml-4"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (isProcessing) {
    // Determine appropriate status message based on progress
    let statusMessage = "Processing your audio...";
    if (progress >= 95) {
      statusMessage = "Almost done! Finalizing your stems...";
    } else if (progress >= 90) {
      statusMessage = "Processing stems, nearly there...";
    } else if (progress >= 60) {
      statusMessage = "Analyzing audio patterns...";
    } else if (progress >= 30) {
      statusMessage = "Separating audio components...";
    } else {
      statusMessage = "Initializing audio processing...";
    }

    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center justify-center gap-2 p-4">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-lg font-medium">{statusMessage}</p>
        </div>
        <div className="w-full bg-card rounded-lg p-4">
          <Progress value={progress} className="w-full h-3" />
          <p className="text-sm text-center text-muted-foreground mt-2">
            {progress}% complete
          </p>
        </div>
        
        {/* Sesh Promo Banner */}
        <a 
          href="https://sesh.fm" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block w-full hover-scale rounded-lg overflow-hidden transition-transform duration-200"
        >
          <img 
            src={seshPromo} 
            alt="Sesh - Multiplayer DAW. Try it for free today" 
            className="w-full h-auto"
          />
        </a>
      </div>
    );
  }

  return null;
};
